import React from 'react';
import {
  Button,
  Collapse,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import { EventLogFilters, ExistingCredential } from '../types';
import { toInputDate } from '../hooks/useEventLogs';

const OPERATION_LABELS: Record<string, string> = {
  EXT_GET_BOOKING: 'Get Booking',
  EXT_SEARCH_BOOKING: 'Search Bookings',
  EXT_APPROVE_DECLINE_BOOKING: 'Approve / Decline Booking',
  EXT_CHECK_IN: 'Check In',
  EXT_CHECK_OUT: 'Check Out',
  EXT_GET_ACTIVITY: 'Get Activity',
  EXT_EDIT_ACTIVITY: 'Edit Activity',
  EXT_INGEST_GATE_EVENTS: 'Ingest Gate Events',
};

export { OPERATION_LABELS };

interface EventLogFilterPanelProps {
  open: boolean;
  filters: EventLogFilters;
  levelFilter: string;
  credentials: ExistingCredential[];
  onFilterChange: (field: keyof EventLogFilters) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDateChange: (field: 'startDate' | 'endDate') => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onLevelFilterChange: (value: string) => void;
  onReset: () => void;
}

const EventLogFilterPanel = ({
  open,
  filters,
  levelFilter,
  credentials,
  onFilterChange,
  onDateChange,
  onLevelFilterChange,
  onReset,
}: EventLogFilterPanelProps) => (
  <Collapse in={open} timeout="auto" unmountOnExit>
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'flex-end',
        mb: 2,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 1,
      }}
    >
      <TextField
        select
        label="API Key"
        value={filters.thirdPartyId}
        onChange={onFilterChange('thirdPartyId')}
        size="small"
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">All Keys</MenuItem>
        {credentials
          .filter(c => c.active)
          .map(c => (
            <MenuItem key={c.id} value={c.id}>
              {c.description || c.key}
            </MenuItem>
          ))}
      </TextField>

      <TextField
        select
        label="Operation"
        value={filters.operationId}
        onChange={onFilterChange('operationId')}
        size="small"
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">All Operations</MenuItem>
        {Object.entries(OPERATION_LABELS).map(([value, label]) => (
          <MenuItem key={value} value={value}>{label}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Level"
        value={levelFilter}
        onChange={e => onLevelFilterChange(e.target.value)}
        size="small"
        sx={{ minWidth: 130 }}
      >
        <MenuItem value="">All Levels</MenuItem>
        <MenuItem value="success">Success (2xx)</MenuItem>
        <MenuItem value="info">Info (3xx)</MenuItem>
        <MenuItem value="warning">Warning (4xx)</MenuItem>
        <MenuItem value="error">Error (5xx)</MenuItem>
      </TextField>

      <TextField
        label="Start Date"
        type="date"
        value={toInputDate(filters.startDate)}
        onChange={onDateChange('startDate')}
        size="small"
        sx={{ minWidth: 160 }}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="End Date"
        type="date"
        value={toInputDate(filters.endDate)}
        onChange={onDateChange('endDate')}
        size="small"
        sx={{ minWidth: 160 }}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        select
        label="Results"
        value={filters.size}
        onChange={onFilterChange('size')}
        size="small"
        sx={{ minWidth: 100 }}
      >
        <MenuItem value={25}>25</MenuItem>
        <MenuItem value={50}>50</MenuItem>
        <MenuItem value={100}>100</MenuItem>
        <MenuItem value={150}>150</MenuItem>
        <MenuItem value={200}>200</MenuItem>
      </TextField>

      <Button
        size="small"
        variant="text"
        onClick={onReset}
        sx={{ color: 'text.secondary', alignSelf: 'center' }}
      >
        Reset
      </Button>
    </Paper>
  </Collapse>
);

export default EventLogFilterPanel;
